import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  allocateLiveLines,
  deriveListeningLabel,
  MAX_LIVE_LINES,
  type KeyThemesViewModel,
} from "./keyThemesView.ts";
import type { ListeningState } from "../../shared/types.ts";

export function KeyThemesStage({
  view,
  listening,
}: {
  view: Extract<KeyThemesViewModel, { kind: "stage" }>;
  listening?: ListeningState | null;
}) {
  const nowRef = useRef<HTMLElement | null>(null);
  const [lineBudget, setLineBudget] = useState(MAX_LIVE_LINES);
  // Convex only pushes on data changes; aging the indicator out needs a clock.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNowMs(Date.now()), 3_000);
    return () => clearInterval(tick);
  }, []);
  const listeningLabel = deriveListeningLabel(listening, nowMs);
  const current = view.current;
  const contentKey = current
    ? [
        current.lead.text,
        String(current.lead.subpoints.length),
        ...current.restCandidates.map((point) => `${point.text}#${point.subpoints.length}`),
      ].join("|")
    : "";

  // Height-fit pass: new content restarts from the full budget, then the
  // measure effect shrinks it one line at a time — all before paint, so the
  // audience only ever sees whole lines ending cleanly above the footer.
  useLayoutEffect(() => {
    setLineBudget(MAX_LIVE_LINES);
  }, [contentKey]);
  useLayoutEffect(() => {
    const el = nowRef.current;
    if (el && el.scrollHeight > el.clientHeight) {
      setLineBudget((budget) => Math.max(1, budget - 1));
    }
  });
  useLayoutEffect(() => {
    const onResize = () => setLineBudget(MAX_LIVE_LINES);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const allocated = current
    ? allocateLiveLines(current.lead.subpoints, current.restCandidates, lineBudget)
    : null;

  return (
    <main className="projector-keythemes" data-testid="projector-keythemes">
      <section className="pkt-now" ref={nowRef}>
        <div className="pkt-now-head">
          <span className="pkt-live-dot" aria-hidden="true" />
          <span className="projector-label pkt-label-now">Live content</span>
          {listeningLabel && (
            <span className="pkt-listening" data-testid="projector-listening">
              <span className="pkt-listening-dot" aria-hidden="true" />
              {listeningLabel}
            </span>
          )}
        </div>
        {current && allocated ? (
          <>
            {/* keyed by content: a new lead remounts and replays the entrance animation */}
            <div className="pkt-block pkt-animate" key={current.lead.text}>
              <div className="pkt-lead">
                {current.lead.text}
                {current.lead.speaker && (
                  <span className="pkt-attr"> ({current.lead.speaker})</span>
                )}
              </div>
              {allocated.leadSubpoints.length > 0 && (
                <div className="pkt-subs">
                  {allocated.leadSubpoints.map((sub, i) => (
                    <div className="pkt-sub" key={i}>{sub}</div>
                  ))}
                </div>
              )}
            </div>
            {allocated.rest.map((point) => (
              <div className="pkt-block pkt-prev pkt-animate" key={point.text}>
                <div className="pkt-lead">
                  {point.text}
                  {point.speaker && <span className="pkt-attr"> ({point.speaker})</span>}
                </div>
                {point.subpoints.length > 0 && (
                  <div className="pkt-subs">
                    {point.subpoints.map((sub, i) => (
                      <div className="pkt-sub" key={i}>{sub}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        ) : (
          <div className="projector-empty">Waiting for the first summary…</div>
        )}
      </section>
      <section className="pkt-themes">
        <span className="projector-label pkt-label-themes">Key themes</span>
        {view.themes.length > 0 ? (
          <div className="pkt-theme-list" data-testid="projector-keythemes-list">
            {view.themes.map((theme) => (
              <div className="pkt-theme" key={theme.key}>
                <h3>{theme.title}</h3>
                {theme.summary && <p>{theme.summary}</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="pkt-themes-empty">Themes are being distilled…</div>
        )}
      </section>
      {/* Shared footer row: both labels live in one grid row so they can never
          misalign, regardless of either panel's content overflowing. */}
      <div className="pkt-foot pkt-foot-now">
        {current ? `last updated · ${current.timeLabel}` : ""}
      </div>
      <div className="pkt-foot pkt-foot-themes">
        {view.themesThroughLabel ? `last updated · ${view.themesThroughLabel}` : ""}
      </div>
    </main>
  );
}
